import React, { useRef, useState } from "react";
import { Plus, X } from "lucide-react";
import { Btn, ErrorNote, Field, Input, Select, Textarea } from "./ui";
import { TimeSelect } from "./TimeSelect";
import { SERVICES, TRIP_TYPES, todayCentral } from "@/lib/format";
import { errMsg } from "@/lib/api";

export type RidePayload = Record<string, any>;

const EMPTY = {
  name: "", phone: "", service: "standard", trip_type: "one_way", pickup_date: "", pickup_time: "",
  pickup_address: "", dropoff_address: "", stops: [] as string[], notes: "", requested_date: "", requested_time: "",
};

export function RideForm({ onSubmit, owner = false, submitLabel, initial, hideContact = false }: {
  onSubmit: (p: RidePayload) => Promise<void>; owner?: boolean; submitLabel: string; initial?: Partial<typeof EMPTY>; hideContact?: boolean;
}) {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const started = useRef(Date.now());
  const set = (k: string, v: any) => setF((s) => ({ ...s, [k]: v }));
  const editOnly = hideContact;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!f.pickup_time) return setError("Choose a pickup time.");
    setBusy(true);
    setError("");
    try {
      const p: RidePayload = { ...f, stops: f.stops.filter((s) => s.trim()) };
      if (!owner) Object.assign(p, { website: hp, elapsed_ms: Date.now() - started.current });
      if (!p.requested_date || !p.requested_time) { delete p.requested_date; delete p.requested_time; }
      await onSubmit(p);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-6" data-testid="ride-form">
      {!editOnly && (
        <div className="grid gap-6 md:grid-cols-2">
          <Field label="Full name" htmlFor="rf-name">
            <Input id="rf-name" required minLength={2} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="ride-form-name" autoComplete="name" />
          </Field>
          <Field label="Mobile phone" htmlFor="rf-phone" hint="We'll text your quote here. No email needed.">
            <Input id="rf-phone" required type="tel" inputMode="tel" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="ride-form-phone" placeholder="(662) 555-0123" autoComplete="tel" />
          </Field>
        </div>
      )}
      <div className="grid gap-6 md:grid-cols-2">
        <Field label="Service" htmlFor="rf-service">
          <Select id="rf-service" options={SERVICES} value={f.service} onChange={(e) => set("service", e.target.value)} data-testid="ride-form-service" />
        </Field>
        <Field label="Trip type" htmlFor="rf-trip">
          <Select id="rf-trip" options={TRIP_TYPES} value={f.trip_type} onChange={(e) => set("trip_type", e.target.value)} data-testid="ride-form-trip-type" />
        </Field>
        <Field label="Pickup date" htmlFor="rf-date">
          <Input id="rf-date" required type="date" min={todayCentral()} value={f.pickup_date} onChange={(e) => set("pickup_date", e.target.value)} data-testid="ride-form-date" />
        </Field>
        <Field label="Pickup time (Central)" htmlFor="rf-time">
          <TimeSelect id="rf-time" value={f.pickup_time} onChange={(v) => set("pickup_time", v)} testid="ride-form-time" />
        </Field>
      </div>
      <Field label="Pickup address" htmlFor="rf-from">
        <Input id="rf-from" required minLength={3} value={f.pickup_address} onChange={(e) => set("pickup_address", e.target.value)} data-testid="ride-form-pickup-address" />
      </Field>
      <Field label="Drop-off address" htmlFor="rf-to">
        <Input id="rf-to" required minLength={3} value={f.dropoff_address} onChange={(e) => set("dropoff_address", e.target.value)} data-testid="ride-form-dropoff-address" />
      </Field>
      <StopsEditor stops={f.stops} onChange={(v) => set("stops", v)} />
      <Field label="Notes" htmlFor="rf-notes" hint="Flight number, luggage, special requests.">
        <Textarea id="rf-notes" maxLength={1000} value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="ride-form-notes" />
      </Field>
      {owner && !hideContact && (
        <div className="grid gap-6 rounded-md border border-gold/20 p-4 md:grid-cols-2">
          <Field label="Actual request date (phone booking)" htmlFor="rf-rdate" hint="Leave blank to use the current time. Changes are logged.">
            <Input id="rf-rdate" type="date" value={f.requested_date} onChange={(e) => set("requested_date", e.target.value)} data-testid="ride-form-requested-date" />
          </Field>
          <Field label="Actual request time" htmlFor="rf-rtime">
            <TimeSelect id="rf-rtime" value={f.requested_time} onChange={(v) => set("requested_time", v)} testid="ride-form-requested-time" />
          </Field>
        </div>
      )}
      {!owner && (
        <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
          <label htmlFor="rf-website">Website</label>
          <input id="rf-website" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} />
        </div>
      )}
      <ErrorNote msg={error} />
      <div>
        <Btn type="submit" disabled={busy} data-testid="ride-form-submit-btn" className="w-full md:w-auto">{busy ? "Sending…" : submitLabel}</Btn>
      </div>
    </form>
  );
}

function StopsEditor({ stops, onChange }: { stops: string[]; onChange: (v: string[]) => void }) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-[14px] font-medium text-mist/90">Stops along the way</span>
      {stops.map((s, i) => (
        <div key={i} className="flex gap-2">
          <Input aria-label={`Stop ${i + 1}`} value={s} onChange={(e) => onChange(stops.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`ride-form-stop-${i}`} />
          <Btn variant="quiet" aria-label="Remove stop" onClick={() => onChange(stops.filter((_, j) => j !== i))} data-testid={`ride-form-stop-remove-${i}`} className="px-3"><X size={18} /></Btn>
        </div>
      ))}
      {stops.length < 6 && (
        <Btn variant="ghost" onClick={() => onChange([...stops, ""])} data-testid="ride-form-add-stop-btn" className="self-start"><Plus size={18} /> Add a stop</Btn>
      )}
    </div>
  );
}
